import { useState, useEffect, useCallback } from 'react';
import { registerSW } from 'virtual:pwa-register';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface UsePwaReturn {
  needRefresh: boolean;
  updateSW: (reloadPage?: boolean) => Promise<void>;
  isOffline: boolean;
  offlineSince: Date | null;
  onlineSince: Date | null;
  isInstallable: boolean;
  installApp: () => Promise<void>;
}

export function usePwa(): UsePwaReturn {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineSince, setOfflineSince] = useState<Date | null>(null);
  const [onlineSince, setOnlineSince] = useState<Date | null>(null);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    let updateReady = false;
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        setNeedRefresh(true);
      },
      onOfflineReady() {
        updateReady = true;
      },
    });

    void updateReady;

    return () => {
      updateSW();
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setOnlineSince(new Date());
    };
    const handleOffline = () => {
      setIsOffline(true);
      setOfflineSince(new Date());
    };
    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeinstallprompt', handleInstallPrompt);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
    };
  }, []);

  const updateSWNow = useCallback(async (reloadPage = true) => {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    setNeedRefresh(false);
    if (reloadPage) {
      window.location.reload();
    }
  }, []);

  const installApp = useCallback(async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setInstallPrompt(null);
    }
  }, [installPrompt]);

  return {
    needRefresh,
    updateSW: updateSWNow,
    isOffline,
    offlineSince,
    onlineSince,
    isInstallable: installPrompt !== null,
    installApp,
  };
}
